import { ReportModel } from '../../../../types';
import { ToolsRoles } from '../../../utils/roles/client-roles';

import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import PublicIcon from '@mui/icons-material/Public';
import PersonIcon from '@mui/icons-material/Person';

interface ReportsPinMenuProps {
  anchorEl: HTMLElement | null;
  report: ReportModel | null;
  initialPinnedState: boolean;
  hasDomainPinPermission: boolean;
  personalPinnedReportIds: number[];
  isSavingDomainPin: boolean;
  isSavingPersonalPin: boolean;
  onClose: () => void;
  onToggleDomainPin: (report: ReportModel) => void;
  onTogglePersonalPin: (report: ReportModel) => void;
}

export default function ReportsPinMenu({
  anchorEl,
  report,
  initialPinnedState,
  hasDomainPinPermission,
  personalPinnedReportIds,
  isSavingDomainPin,
  isSavingPersonalPin,
  onClose,
  onToggleDomainPin,
  onTogglePersonalPin,
}: ReportsPinMenuProps) {
  if (!report) return null;

  const isSaving = isSavingDomainPin || isSavingPersonalPin;
  const isDomainPinned = report.domainPinnedAt != null;

  const shouldShowUnpin = initialPinnedState;
  const shouldDisableUnpin =
    (initialPinnedState && isDomainPinned && !hasDomainPinPermission) ||
    isSaving;

  const shouldShowPinPersonally = !initialPinnedState;
  const shouldShowPinDomain = !initialPinnedState;
  const shouldDisablePinDomain =
    (!initialPinnedState && !hasDomainPinPermission) || isSaving;

  return (
    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
      {shouldShowUnpin && (
        <Tooltip
          title={
            initialPinnedState && isDomainPinned && !hasDomainPinPermission
              ? `You do not have the "${ToolsRoles.DomainPinReports.name}" permission to perform this action.`
              : ''
          }
          arrow
        >
          <span>
            <MenuItem
              disabled={shouldDisableUnpin}
              onClick={() => {
                if (isDomainPinned) {
                  onToggleDomainPin(report);
                } else if (personalPinnedReportIds.includes(report.id)) {
                  onTogglePersonalPin(report);
                }
              }}
            >
              <ListItemIcon>
                {isSaving ? (
                  <CircularProgress size={16} />
                ) : (
                  <RemoveCircleOutlineOutlinedIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>Unpin</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
      )}

      {shouldShowPinPersonally && (
        <MenuItem
          disabled={isSavingPersonalPin}
          onClick={() => {
            onTogglePersonalPin(report);
          }}
        >
          <ListItemIcon>
            {isSavingPersonalPin ? (
              <CircularProgress size={16} />
            ) : (
              <PersonIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>Pin Personally</ListItemText>
        </MenuItem>
      )}

      {shouldShowPinDomain && (
        <Tooltip
          title={
            !initialPinnedState && !hasDomainPinPermission
              ? `You do not have the "${ToolsRoles.DomainPinReports.name}" permission to perform this action.`
              : ''
          }
          arrow
        >
          <span>
            <MenuItem
              disabled={shouldDisablePinDomain}
              onClick={() => {
                onToggleDomainPin(report);
              }}
            >
              <ListItemIcon>
                {isSavingDomainPin ? (
                  <CircularProgress size={16} />
                ) : (
                  <PublicIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>Pin for Domain</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
      )}
    </Menu>
  );
}
